@if (interventionHistories().length) {
  <ol
    class="history relative w-full max-w-[500px] m-auto mt-2 border-s border-gray-400"
    data-testid="history"
  >
    @for (interventionHistory of interventionHistories(); track interventionHistory.date) {
      <li
        class="mb-10 ms-5"
        data-testid="intervention-group-by-date"
      >
        <div class="absolute w-6 h-6 bg-green-800 rounded-full -start-3"></div>
        <time class="mb-1 ml-1 text-2xl font-bold leading-none text-gray-800">{{ interventionHistory.date }}</time>
        @for (name of interventionHistory.names; track name.name) {
          <div
            class="intervention"
            data-testid="intervention-group-by-name"
          >
            <div class="flex justify-end gap-2 text-sm font-semibold text-gray-900 text-right">
              <i class="pi pi-user"></i>
              <span>{{ name.name }}</span>
            </div>

            @for (intervention of name.interventions; track intervention.id) {
              @if (!intervention.disablingDate) {
                <div
                  class="intervention-item my-2 bg-gray-100 rounded-xl p-4 px-5 border-l-8"
                  [attr.data-testid]="getInterventionItemTestId(intervention)"
                  [attr.data-intervention-id]="intervention.id"
                >
                  @if (!isReadOnly()) {
                    <div class="flex justify-between items-center gap-2 mb-2">
                      <div class="validation-status">
                        @if (!intervention?.validatedDate) {
                          <bnd-button
                            *isGranted="[PERMISSION.INTERVENTIONS_EDIT]"
                            size="sm"
                            color="primary"
                            (click)="validateIntervention(intervention)"
                            [attr.data-testid]="'validateInterventionButton'"
                          >
                            {{ 'INTERVENTION.VALIDATE' | translate }}
                          </bnd-button>
                        } @else {
                          <span [title]="getValidatorTitle(intervention)">
                            <i
                              class="pi pi-check-circle text-primary"
                              style="font-size: 1.5rem"
                            ></i>
                          </span>
                        }
                      </div>

                      <div class="actions flex gap-4">
                        @if (!intervention?.validatedDate) {
                          <bnd-button
                            *isGranted="[PERMISSION.INTERVENTIONS_EDIT]"
                            shape="circle"
                            size="sm"
                            color="primary"
                            mode="ghost"
                            (click)="editIntervention(intervention)"
                            [attr.data-testid]="'edit-intervention'"
                          >
                            <i class="pi pi-pencil text-green-500"></i>
                          </bnd-button>

                          <bnd-button
                            *isGranted="[PERMISSION.CROPS_DELETE]"
                            shape="circle"
                            size="sm"
                            color="error"
                            mode="ghost"
                            (click)="removeIntervention(intervention)"
                            [attr.data-testid]="'disable-intervention'"
                          >
                            <i class="pi pi-trash text-red-600"></i>
                          </bnd-button>
                        }
                      </div>
                    </div>
                  }

                  <!-- INLINE DELETE CONFIRMATION -->
                  @if (pendingRemovalId() === intervention.id) {
                    <div
                      class="flex flex-col gap-3 my-2 bg-red-50 border border-red-200 rounded-xl p-3"
                      [attr.data-testid]="'disable-intervention-confirm'"
                    >
                      <span class="text-sm font-medium text-red-800">
                        {{ 'INTERVENTION.DISABLING_MESSAGE' | translate }}
                      </span>
                      <div class="flex justify-end gap-3">
                        <bnd-button
                          size="sm"
                          color="error"
                          mode="ghost"
                          (click)="cancelRemoveIntervention()"
                          [attr.data-testid]="'disable-intervention-cancel'"
                        >
                          {{ 'DIALOG.BUTTON.CANCEL' | translate }}
                        </bnd-button>
                        <bnd-button
                          size="sm"
                          color="error"
                          (click)="confirmRemoveIntervention(intervention)"
                          [attr.data-testid]="'disable-intervention-confirm-button'"
                        >
                          {{ 'DIALOG.BUTTON.OK' | translate }}
                        </bnd-button>
                      </div>
                    </div>
                  }

                  <!-- INLINE VALIDATE CONFIRMATION -->
                  @if (pendingValidationId() === intervention.id) {
                    <div
                      class="flex flex-col gap-3 my-2 bg-green-50 border border-green-200 rounded-xl p-3"
                      [attr.data-testid]="'validate-intervention-confirm'"
                    >
                      <span class="text-sm font-medium text-green-800">
                        {{ 'INTERVENTION.VALIDATE_CONFIRMATION_MESSAGE' | translate }}
                      </span>
                      <div class="flex justify-end gap-3">
                        <bnd-button
                          size="sm"
                          color="primary"
                          mode="ghost"
                          (click)="cancelValidateIntervention()"
                          [attr.data-testid]="'validate-intervention-cancel'"
                        >
                          {{ 'DIALOG.BUTTON.CANCEL' | translate }}
                        </bnd-button>
                        <bnd-button
                          size="sm"
                          color="primary"
                          (click)="confirmValidateIntervention(intervention)"
                          [attr.data-testid]="'validate-intervention-confirm-button'"
                        >
                          {{ 'DIALOG.BUTTON.OK' | translate }}
                        </bnd-button>
                      </div>
                    </div>
                  }
                  <!-- FOOD SAFETY -->
                  @if (intervention.type === interventionTypes.FOODSAFETY) {
                    <food-safety [intervention]="intervention"></food-safety>
                  }

                  <!-- APPLICATIONS -->
                  @if (intervention.type === interventionTypes.APPLICATIONS) {
                    @if (intervention.subType === interventionApplicationsTypes.PESTICIDE) {
                      <pesticide [intervention]="intervention"></pesticide>
                    }
                    @if (intervention.subType === interventionApplicationsTypes.IRRIGATION) {
                      <irrigation [intervention]="intervention"></irrigation>
                    }
                    @if (intervention.subType === interventionApplicationsTypes.NUTRIENTS) {
                      <nutrients [intervention]="intervention"></nutrients>
                    }
                  }
                  <!-- OPERATIONS -->
                  @if (intervention.type === interventionTypes.OPERATIONS) {
                    <operation [intervention]="intervention"></operation>
                  }

                  <!-- Files -->
                  @if (intervention.documents?.length) {
                    @for (document of intervention.documents; track document.id) {
                      @if (document.mimeType && allowedFileMimeType.includes(document.mimeType)) {
                        <div
                          class="flex items-center my-2.5 bg-gray-50 rounded-xl p-2 hover:cursor-pointer hover:bg-white"
                          (click)="openFile(intervention.id, document.id)"
                        >
                          <span class="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <i class="pi pi-file-pdf"></i>
                            {{ document.description }}
                          </span>
                        </div>
                      }
                      @if (document.mimeType && allowedImageMimeType.includes(document.mimeType)) {
                        <div
                          class="flex items-center my-2.5 bg-gray-50 rounded-xl p-2 hover:cursor-pointer hover:bg-white"
                          (click)="openFile(intervention.id, document.id)"
                        >
                          <span class="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <i class="pi pi-link"></i>
                            {{ document.mimeType }}
                          </span>
                        </div>
                      }
                    }
                  }
                </div>
              }
            }
          </div>
        }
      </li>
    }
  </ol>
} @else {
  <div class="font-bold justify-center flex">{{ 'INTERVENTION.NO_DATA' | translate }}</div>
}
